import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, ClipboardList, PlusCircle, Settings, LogOut, Columns3, Layers, ScanLine, Wrench } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import NotificationsBell from "@/components/NotificationsBell";

function NavItem({ to, icon: Icon, label, testid, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 text-sm rounded-sm transition-colors duration-150 border-l-2 ${
          isActive
            ? "bg-[#efece5] text-[#424e43] border-[#647265]"
            : "text-[#6b6a68] border-transparent hover:bg-[#efece5] hover:text-[#232826]"
        }`
      }
    >
      <Icon size={18} strokeWidth={1.8} />
      <span className="font-medium">{label}</span>
    </NavLink>
  );
}

function KioskNavItem({ to, icon: Icon, label, testid, end, featured = false }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `flex min-h-20 flex-1 flex-col items-center justify-center gap-1 border transition-colors duration-150 active:scale-[0.97] ${
          featured
            ? "border-[#424e43] bg-[#424e43] text-[#f7f5f1]"
            : isActive
              ? "border-[#647265] bg-[#e6e5df] text-[#232826]"
              : "border-[#cfcfc9] bg-white text-[#6b6a68] hover:bg-[#efece5]"
        }`
      }
    >
      <Icon size={26} strokeWidth={1.8} />
      <span className="text-xs font-bold uppercase tracking-[0.16em]">{label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const isKiosk = user?.role === "kiosk";
  const isMaintenance = user?.role === "maintenance";
  const items = isMaintenance
    ? [{ to: "/maintenance", icon: Wrench, label: "Maintenance Clearing", tid: "nav-maintenance" }]
    : isKiosk
    ? [
        { to: "/board", icon: Columns3, label: "Board", tid: "nav-board" },
        { to: "/rack", icon: Layers, label: "Rack", tid: "nav-rack" },
        { to: "/maintenance", icon: Wrench, label: "Maintenance", tid: "nav-maintenance" },
        { to: "/scan", icon: ScanLine, label: "Scan", tid: "nav-scan" },
      ]
    : [
        { to: "/", icon: LayoutDashboard, label: "Dashboard", tid: "nav-dashboard", end: true },
        { to: "/board", icon: Columns3, label: "Board", tid: "nav-board" },
        { to: "/register", icon: ClipboardList, label: "Rework Register", tid: "nav-register" },
        { to: "/rack", icon: Layers, label: "Rack Clearing", tid: "nav-rack" },
        { to: "/maintenance", icon: Wrench, label: "Maintenance Clearing", tid: "nav-maintenance" },
        { to: "/scan", icon: ScanLine, label: "Scan", tid: "nav-scan" },
      ];
  if (user?.role && !["management", "kiosk", "maintenance"].includes(user.role)) {
    items.splice(3, 0, { to: "/new", icon: PlusCircle, label: "New Request", tid: "nav-new" });
  }
  if (user?.role === "admin") {
    items.push({ to: "/settings", icon: Settings, label: "Settings", tid: "nav-settings" });
  }
  if (user?.role === "management") {
    const si = items.findIndex((i) => i.to === "/scan");
    if (si >= 0) items.splice(si, 1);
  }

  if (isKiosk) {
    return (
      <div className="min-h-screen bg-[#f7f5f1] text-[#232826]" data-testid="kiosk-layout">
        <main className="min-w-0 pb-28">
          <Outlet />
        </main>
        <nav
          data-testid="kiosk-navigation"
          className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 gap-px border-t border-[#cfcfc9] bg-[#cfcfc9] p-2"
        >
          {items.map((item) => (
            <KioskNavItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
              testid={`kiosk-${item.tid}`}
              end={item.end}
              featured={item.to === "/scan"}
            />
          ))}
          <button
            type="button"
            data-testid="kiosk-exit"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="flex min-h-20 flex-1 flex-col items-center justify-center gap-1 border border-[#cfcfc9] bg-white text-[#6b6a68] transition-colors duration-150 hover:bg-[#efece5] active:scale-[0.97]"
          >
            <LogOut size={26} strokeWidth={1.8} />
            <span className="text-xs font-bold uppercase tracking-[0.16em]">Exit</span>
          </button>
        </nav>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#f7f5f1] text-[#232826]">
      {/* Sidebar - desktop */}
      <aside className="hidden md:flex md:w-64 flex-col border-r border-[#e6e5df] bg-[#f7f5f1] sticky top-0 h-screen">
        <div className="px-5 py-6 border-b border-[#e6e5df]">
          <img
            src="/misco-logo.webp"
            alt="MISCO"
            data-testid="brand-logo"
            className="h-10 w-auto"
          />
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#647265] mt-3 font-mono">Rework Hub</div>
        </div>
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {items.map((it) => (
            <NavItem key={it.to} to={it.to} icon={it.icon} label={it.label} testid={it.tid} end={it.end} />
          ))}
          {!isKiosk && <NotificationsBell />}
        </nav>
        <div className="px-4 py-4 border-t border-[#e6e5df]">
          <div className="flex items-center justify-between mb-3">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate text-[#232826]" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-[#6b6a68] truncate">{user?.email}</div>
            </div>
            <Badge className="bg-[#e6e5df] text-[#424e43] border border-[#647265]/30 rounded-sm text-[10px] uppercase" data-testid="user-role">{ROLE_LABEL[user?.role] || user?.role}</Badge>
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="w-full rounded-sm bg-[#e6e5df] hover:bg-[#cfcfc9] text-[#232826] border border-[#cfcfc9]"
            data-testid="logout-btn"
            onClick={async () => { await logout(); navigate("/login"); }}
          >
            <LogOut size={14} className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 pb-20 md:pb-0 bg-[#f7f5f1]">
        <Outlet />
      </main>

      {/* Bottom Nav - mobile */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 border-t border-[#e6e5df] bg-[#f7f5f1]/95 backdrop-blur z-40 flex justify-around">
        {items.slice(0, 5).map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={`mobile-${it.tid}`}
            className={({ isActive }) =>
              `flex-1 py-3 flex flex-col items-center gap-1 text-[10px] uppercase tracking-widest ${
                isActive ? "text-[#424e43]" : "text-[#a39f98]"
              }`
            }
          >
            <it.icon size={18} />
            {it.label.split(" ")[0]}
          </NavLink>
        ))}
        <button
          data-testid="mobile-logout-btn"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="flex-1 py-3 flex flex-col items-center gap-1 text-[10px] uppercase tracking-widest text-[#a39f98]"
        >
          <LogOut size={18} /> Exit
        </button>
      </nav>
    </div>
  );
}
